import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SubscriptionFilters } from "@/components/subscriptions/SubscriptionFilters";
import { CreditCard, FileText, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { TenantDetailsModal } from "@/components/admin/TenantDetailsModal";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import { startOfDay, endOfDay, subDays } from "date-fns";
import { useRef } from "react";
import TenantDetails from "@/pages/TenantDetails";
import { parseISO, isAfter, format as formatDateFns } from "date-fns";

interface TenantRow {
  id: string;
  name: string;
  email: string | null;
  subscription_status: string | null;
  subscription_plan_id: string | null;
  subscription_type: string | null;
  subscription_payment_method: string | null;
  subscription_start_date: string | null;
  subscription_next_billing_date: string | null;
  subscription_last_payment_date: string | null;
}

interface FiltersState {
  searchTerm: string;
  status: string[];
  types: string[];
  plans: string[];
  methods: string[];
  dateFrom: string;
  dateTo: string;
  tenantIds: string[];
}

const Subscriptions = () => {
  const navigate = useNavigate();
  const { isAdminSaas } = useTenant();
  const [filters, setFilters] = useState<FiltersState>({
    searchTerm: "",
    status: [],
    types: [],
    plans: [],
    methods: [],
    dateFrom: "",
    dateTo: "",
    tenantIds: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);
  const [tenantProfile, setTenantProfile] = useState<{ id: string; name: string } | null>(null);

  const { data: availablePlans = [] } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("id, name")
        .eq("status", "active")
        .order("price", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ["all-subscription-transactions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("id, tenant_id, status, expiration_date, payment_date")
        .eq("status", "approved")
        .order("expiration_date", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: tenants, isLoading } = useQuery({
    queryKey: ["subscriptions-tenants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select(`
          id, name, email,
          subscription_status,
          subscription_plan_id,
          subscription_type,
          subscription_payment_method,
          subscription_start_date,
          subscription_next_billing_date,
          subscription_last_payment_date
        `)
        .order("name", { ascending: true }) as any;
      if (error) throw error;
      return (data || []) as TenantRow[];
    },
    enabled: isAdminSaas,
  });

  const getPlanName = (planId: string | null) => {
    if (!planId) return "Trial";
    const plan = availablePlans.find((p: any) => p.id === planId);
    return plan?.name || planId;
  };

  const matchesFilters = (t: TenantRow) => {
    if (filters.searchTerm) {
      const s = filters.searchTerm.toLowerCase();
      const ok = t.name.toLowerCase().includes(s) || (t.email || "").toLowerCase().includes(s);
      if (!ok) return false;
    }
    if (filters.tenantIds.length > 0) {
      if (!filters.tenantIds.includes(t.id)) return false;
    }
    if (filters.status.length > 0) {
      if (!t.subscription_status || !filters.status.includes(t.subscription_status)) return false;
    }
    if (filters.types.length > 0) {
      const type = t.subscription_type || "regular";
      if (!filters.types.includes(type)) return false;
    }
    if (filters.plans.length > 0) {
      const planKey = t.subscription_plan_id || "trial";
      if (!filters.plans.includes(planKey)) return false;
    }
    if (filters.methods.length > 0) {
      const method = t.subscription_payment_method || "-";
      if (!filters.methods.includes(method)) return false;
    }
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      const lastPay = t.subscription_last_payment_date ? new Date(t.subscription_last_payment_date) : null;
      if (!lastPay || lastPay < from) return false;
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      const lastPay = t.subscription_last_payment_date ? new Date(t.subscription_last_payment_date) : null;
      if (!lastPay || lastPay > to) return false;
    }
    return true;
  };

  const filteredTenants = useMemo(() => {
    if (!tenants) return [] as TenantRow[];
    return tenants.filter((t) => matchesFilters(t));
  }, [tenants, filters]);

  const facetCounts = useMemo(() => {
    const counts = {
      status: {} as Record<string, number>,
      types: {} as Record<string, number>,
      plans: {} as Record<string, number>,
      methods: {} as Record<string, number>,
      tenants: {} as Record<string, number>,
    };
    if (!tenants) return counts;
    const matchesOtherFiltersExcept = (tenant: TenantRow, except?: "status" | "types" | "plans" | "methods" | "tenants") => {
      const s = filters.searchTerm ? filters.searchTerm.toLowerCase() : "";
      if (s) {
        const ok = tenant.name.toLowerCase().includes(s) || (tenant.email || "").toLowerCase().includes(s);
        if (!ok) return false;
      }
      if (except !== "tenants" && filters.tenantIds.length > 0) {
        if (!filters.tenantIds.includes(tenant.id)) return false;
      }
      if (except !== "status" && filters.status.length > 0) {
        if (!tenant.subscription_status || !filters.status.includes(tenant.subscription_status)) return false;
      }
      if (except !== "types" && filters.types.length > 0) {
        const type = tenant.subscription_type || "regular";
        if (!filters.types.includes(type)) return false;
      }
      if (except !== "plans" && filters.plans.length > 0) {
        const planKey = tenant.subscription_plan_id || "trial";
        if (!filters.plans.includes(planKey)) return false;
      }
      if (except !== "methods" && filters.methods.length > 0) {
        const method = tenant.subscription_payment_method || "-";
        if (!filters.methods.includes(method)) return false;
      }
      if (filters.dateFrom) {
        const from = new Date(filters.dateFrom);
        const lastPay = tenant.subscription_last_payment_date ? new Date(tenant.subscription_last_payment_date) : null;
        if (!lastPay || lastPay < from) return false;
      }
      if (filters.dateTo) {
        const to = new Date(filters.dateTo);
        const lastPay = tenant.subscription_last_payment_date ? new Date(tenant.subscription_last_payment_date) : null;
        if (!lastPay || lastPay > to) return false;
      }
      return true;
    };
    tenants.forEach((t) => {
      const include = matchesOtherFiltersExcept({ ...t });
      if (!include) return;
      const status = t.subscription_status || "trial";
      counts.status[status] = (counts.status[status] || 0) + 1;
      const type = t.subscription_type || "regular";
      counts.types[type] = (counts.types[type] || 0) + 1;
      const plan = t.subscription_plan_id || "trial";
      counts.plans[plan] = (counts.plans[plan] || 0) + 1;
      const method = t.subscription_payment_method || "-";
      counts.methods[method] = (counts.methods[method] || 0) + 1;
      counts.tenants[t.id] = (counts.tenants[t.id] || 0) + 1;
    });
    return counts;
  }, [tenants, filters]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTenants = filteredTenants.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredTenants.length / itemsPerPage);

  const getStatusBadge = (status: string) => {
    if (status === "active") return { variant: "default" as const, className: "bg-green-600 hover:bg-green-700 text-white" };
    if (status === "trial") return { variant: "secondary" as const, className: "bg-gray-500 hover:bg-gray-600 text-white" };
    if (status === "suspended") return { variant: "destructive" as const, className: "bg-red-600 hover:bg-red-700 text-white" };
    if (status === "cancelled") return { variant: "outline" as const, className: "bg-gray-400 hover:bg-gray-500 text-white" };
    return { variant: "outline" as const, className: "" };
  };

  const getActiveUntilDate = (tenantId: string, t: TenantRow) => {
    const now = new Date();
    const txs = transactions.filter((x: any) => x.tenant_id === tenantId);
    const lifetime = txs.find((x: any) => x.expiration_date === null && x.status === "approved");
    if (lifetime) return null;
    const activeWithExp = txs
      .filter((x: any) => {
        if (!x.expiration_date) return false;
        const exp = parseISO(x.expiration_date);
        return isAfter(exp, now);
      })
      .sort((a: any, b: any) => {
        const da = parseISO(a.expiration_date);
        const db = parseISO(b.expiration_date);
        return db.getTime() - da.getTime();
      });
    if (activeWithExp.length > 0) return parseISO(activeWithExp[0].expiration_date);
    if (t.subscription_next_billing_date) {
      const next = parseISO(t.subscription_next_billing_date);
      if (isAfter(next, now)) return next;
    }
    if (t.subscription_last_payment_date) {
      const last = parseISO(t.subscription_last_payment_date);
      if (isAfter(last, now)) return last;
    }
    return null;
  };

  const formatActiveUntil = (t: TenantRow) => {
    const d = getActiveUntilDate(t.id, t);
    if (d === null) return "Expirado";
    return formatDateFns(d, "dd/MM/yyyy");
  };

  const allStatuses = useMemo(() => {
    const set = new Set<string>();
    tenants?.forEach((t) => set.add(t.subscription_status || "trial"));
    return Array.from(set);
  }, [tenants]);

  const allTypes = ["regular", "cortesia", "testers", "promotion"];
  const allPlans = useMemo(() => {
    const values = new Set<string>(["trial"]);
    availablePlans.forEach((p: any) => values.add(p.id));
    return Array.from(values);
  }, [availablePlans]);
  const allMethods = ["manual", "pix", "credit", "cortesia", "testers", "promotion", "-"];
  const tenantOptions = useMemo(() => {
    return (tenants || []).map((t) => ({ id: t.id, label: t.name }));
  }, [tenants]);

  if (!isAdminSaas) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Acesso restrito</h1>
        <p className="text-muted-foreground">Somente Admin SaaS pode visualizar esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CreditCard className="h-8 w-8" />
            Assinaturas
          </h1>
          <p className="text-muted-foreground">Gerencie todas as assinaturas do sistema</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Lista de Assinaturas</CardTitle>
                <CardDescription>
                  {filteredTenants.length} {filteredTenants.length === 1 ? "assinatura" : "assinaturas"}
                </CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[220px]">
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => {
                      setFilters({ ...filters, searchTerm: e.target.value });
                      setCurrentPage(1);
                    }}
                    className="pl-2"
                  />
                </div>
                <SubscriptionFilters
                  filters={filters}
                  onFiltersChange={(f) => { setFilters(f); setCurrentPage(1); }}
                  statuses={allStatuses}
                  types={allTypes}
                  planOptions={[{ id: "trial", label: "Trial" }, ...availablePlans.map((p: any) => ({ id: p.id, label: p.name }))]}
                  methods={allMethods}
                  counts={facetCounts}
                  tenants={tenantOptions}
                />
              </div>
            </div>
            <div className="text-[13px] md:text-sm text-muted-foreground">
              <span className="mr-2 font-bold">Estatísticas:</span>
              <div className="inline-flex flex-wrap gap-1">
                {Object.entries(facetCounts.status).map(([k, v]) => (
                  <Badge key={`status-${k}`} variant="secondary">Status {k}: {v}</Badge>
                ))}
                {Object.entries(facetCounts.types).map(([k, v]) => (
                  <Badge key={`type-${k}`} variant="secondary">Tipo {k}: {v}</Badge>
                ))}
                {Object.entries(facetCounts.plans).map(([k, v]) => (
                  <Badge key={`plan-${k}`} variant="secondary">Plano {getPlanName(k === "trial" ? null : k)}: {v}</Badge>
                ))}
                {Object.entries(facetCounts.methods).map(([k, v]) => (
                  <Badge key={`method-${k}`} variant="secondary">Método {k}: {v}</Badge>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-center">Plano</TableHead>
                      <TableHead className="text-center">Tipo</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead>Ativo até</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTenants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma assinatura encontrada</TableCell>
                      </TableRow>
                    ) : (
                      paginatedTenants.map((tenant) => {
                        const planId = tenant.subscription_plan_id || "trial";
                        const planName = getPlanName(tenant.subscription_plan_id);
                        const typeLabel = tenant.subscription_type || "regular";
                        const status = tenant.subscription_status || "trial";
                        const statusBadge = getStatusBadge(status);
                        return (
                          <TableRow key={tenant.id}>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium">{tenant.name}</div>
                                {tenant.email && <div className="text-xs text-muted-foreground">{tenant.email}</div>}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={planId === "trial" ? "secondary" : "default"}>{planName}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">{typeLabel}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={statusBadge.variant} className={statusBadge.className}>{status}</Badge>
                            </TableCell>
                            <TableCell>{formatActiveUntil(tenant)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center gap-2">
                                <Button variant="ghost" size="icon" className="icon-chip-blue" onClick={() => setTenantProfile({ id: tenant.id, name: tenant.name })} title="Ver dados do cliente">
                                  <FileText className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="icon-chip-green" onClick={() => { setSelectedTenant(tenant); setDetailsModalOpen(true); }} title="Ver transações e editar">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages || 1}
                itemsPerPage={itemsPerPage}
                totalItems={filteredTenants.length}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {selectedTenant && (
        <TenantDetailsModal
          open={detailsModalOpen}
          onOpenChange={(open) => setDetailsModalOpen(open)}
          tenant={{
            id: selectedTenant.id,
            name: selectedTenant.name,
            slug: "",
            email: selectedTenant.email,
            status: selectedTenant.subscription_status || "trial",
            trial_ends_at: null,
            created_at: new Date().toISOString(),
            subscription_status: selectedTenant.subscription_status,
            subscription_plan_id: selectedTenant.subscription_plan_id,
            subscription_type: selectedTenant.subscription_type,
            subscription_payment_method: selectedTenant.subscription_payment_method,
            subscription_start_date: selectedTenant.subscription_start_date,
            subscription_next_billing_date: selectedTenant.subscription_next_billing_date,
            subscription_last_payment_date: selectedTenant.subscription_last_payment_date,
            colors: null,
            images: null,
          }}
        />
      )}

      {(typeof tenantProfile !== "undefined") && tenantProfile && (
        <RaioXModal
          open={!!tenantProfile}
          onOpenChange={(open) => { if (!open) setTenantProfile(null); }}
          entityTypeLabel={"CLIENTE SAAS"}
          entityName={tenantProfile.name}
          infoItems={[]}
          showPeriodControls
          initialFrom={startOfDay(subDays(new Date(), 6))}
          initialTo={endOfDay(new Date())}
          contentClassName="p-4"
          renderContent={({ periodRange, setLoading }) => (
            <TenantDetails id={tenantProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
          )}
        />
      )}
    </div>
  );
};

export default Subscriptions;
